/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * DashboardLayout Component - Dashboard pages layout
 */

import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';
import MobileBottomNav from '@components/common/MobileBottomNav';
import PwaInstallPrompt from '@components/common/PwaInstallPrompt';
import logoImg from '@assets/images/vowsera_256x256.png';
import NotificationBell from '@components/common/NotificationBell';
import LanguageSwitcher from '@components/common/LanguageSwitcher';
import PlanBadge from '@components/common/PlanBadge';
import { usePlan } from '@context/PlanContext';
import { WeddingProvider } from '@context/WeddingContext';
import GlobalSearchBar from '@components/common/GlobalSearchBar';
import AdminSearchBar from '@components/common/AdminSearchBar';
import RealtimeToastOverlay from '@components/common/RealtimeToastOverlay';
import NoWeddingPrompt from '@components/common/NoWeddingPrompt';
import PromoBannerDisplay from '@components/common/PromoBannerDisplay';
import coordinatorService from '@services/coordinatorService';
import vendorPortalService from '@services/vendorPortalService';
import { resolveStorageUrl } from '@utils/storageUrl';
import { getActingWedding, stopActing, isActingPath } from '@utils/actingWedding';
import './DashboardLayout.scss';

const DashboardLayout = ({ type = 'couple' }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { hasFeature, plan } = usePlan();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [closedGroups, setClosedGroups] = useState({});
  const [userDropdown, setUserDropdown] = useState(false);
  // A planner working inside a client's wedding in this tab.
  const acting = type === 'couple' && user?.role === 'coordinator' ? getActingWedding() : null;
  const leaveWedding = () => { stopActing(); navigate('/coordinator/weddings'); };
  // Hydrate the white-label config from sessionStorage on first mount so
  // the brand name/logo paint instantly on navigation instead of
  // flickering off and on while the API call resolves. The fetch below
  // then refreshes the cache in the background. Without this the
  // coordinator's "My Coordinator" brand text would visibly disappear
  // and re-appear on every route change in their portal.
  const WL_CACHE_KEY = 'vowsera:white-label-cache';
  const [whiteLabel, setWhiteLabel] = useState(() => {
    // The planner's branding also follows them into a client's wedding, so a
    // client watching their screen sees the studio's brand throughout.
    if (type !== 'coordinator' && !acting) return null;
    try {
      const raw = sessionStorage.getItem(WL_CACHE_KEY);
      const cached = raw ? JSON.parse(raw) : null;
      return cached && cached.isActive !== false ? cached : null;
    } catch { return null; }
  });
  // Vendor plan comes from vendor_subscriptions (free/pro/premium), NOT from
  // the couple billing plan PlanContext exposes — those are different tiers.
  const [vendorPlan, setVendorPlan] = useState(null);
  useEffect(() => {
    if (type !== 'vendor') return;
    vendorPortalService.getMySubscription()
      .then(r => setVendorPlan(r?.data?.planSlug || 'free'))
      .catch(() => setVendorPlan('free'));
  }, [type]);

  // Coordinator portal theming — load the coordinator's white-label config
  // and re-theme the shell. `is_active === false` disables branding.
  // We also persist whatever comes back into sessionStorage so the next
  // page paint inside this portal uses the cache as a stable initial
  // value (see useState initializer above).
  useEffect(() => {
    if (type !== 'coordinator' && !acting) return;
    const apply = (cfg) => {
      const next = cfg && cfg.isActive !== false ? cfg : null;
      setWhiteLabel(next);
      try {
        if (next) sessionStorage.setItem(WL_CACHE_KEY, JSON.stringify(next));
        else sessionStorage.removeItem(WL_CACHE_KEY);
      } catch { /* storage full / disabled — ignore */ }
    };
    coordinatorService.getWhiteLabel().then(r => apply(r?.data)).catch(() => {});
    const onUpdate = (e) => apply(e.detail);
    window.addEventListener('vowsera:white-label-updated', onUpdate);
    return () => window.removeEventListener('vowsera:white-label-updated', onUpdate);
  }, [type]);

  const wlPrimary = /^#[0-9a-fA-F]{6}$/.test(whiteLabel?.primaryColor || '') ? whiteLabel.primaryColor : null;
  const wlLogo = whiteLabel?.logoUrl ? resolveStorageUrl(whiteLabel.logoUrl) : null;
  const wlBrand = whiteLabel?.brandName || null;
  const wlAccent   = /^#[0-9a-fA-F]{6}$/.test(whiteLabel?.accentColor || '') ? whiteLabel.accentColor : null;
  const wlHeaderBg = /^#[0-9a-fA-F]{6}$/.test(whiteLabel?.headerBgColor || '') ? whiteLabel.headerBgColor : null;
  const wlFooter   = whiteLabel?.footerText || null;
  const wlFavicon  = whiteLabel?.faviconUrl || null;
  // Readable header foreground for a custom header background (luminance-based).
  const wlHeaderText = wlHeaderBg ? (() => {
    const r = parseInt(wlHeaderBg.slice(1, 3), 16), g = parseInt(wlHeaderBg.slice(3, 5), 16), b = parseInt(wlHeaderBg.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 145 ? '#FFFFFF' : '#1F2937';
  })() : null;

  // Apply the coordinator's custom favicon to the browser tab; restore on change.
  useEffect(() => {
    if (!wlFavicon) return;
    const links = Array.from(document.querySelectorAll('link[rel*="icon"]'));
    const original = links.map(l => l.getAttribute('href'));
    links.forEach(l => l.setAttribute('href', wlFavicon));
    return () => links.forEach((l, i) => { if (original[i] != null) l.setAttribute('href', original[i]); });
  }, [wlFavicon]);

  const toggleGroup = (label) => {
    const allLabels = activeNavGroups.filter(g => g.label).map(g => g.label);
    const isCurrentlyOpen = !closedGroups[label];
    const newClosed = {};
    allLabels.forEach(l => { newClosed[l] = true; });
    if (!isCurrentlyOpen) newClosed[label] = false;
    setClosedGroups(newClosed);
  };

  const handleLogout = async () => {
    try { sessionStorage.removeItem(WL_CACHE_KEY); } catch { /* ignore */ }
    await logout();
    navigate('/login');
  };

  // ── Couple nav groups (10 groups) ────────────────────────────────────────
  const coupleNavGroups = [
    {
      label: null, // pinned — no header
      items: [
        { to: '/dashboard', icon: 'bi-columns-gap', label: t('nav.dashboard'), end: true },
        { to: '/dashboard/my-wedding', icon: 'bi-heart', label: t('nav.myWedding') },
      ],
    },
    {
      label: t('nav.groupPlanning'),
      items: [
        { to: '/dashboard/checklist', icon: 'bi-check2-square', label: t('nav.checklist') },
        { to: '/dashboard/budget', icon: 'bi-wallet2', label: t('nav.budget') },
        { to: '/dashboard/destination-calculator', icon: 'bi-airplane', label: t('nav.destinationCalculator'), plan: 'gold' },
        { to: '/dashboard/events', icon: 'bi-calendar-event', label: t('nav.weddingEvents') },
        { to: '/dashboard/wedding-timeline', icon: 'bi-clock-history', label: t('nav.dayTimeline'), plan: 'silver' },
        { to: '/dashboard/wedding-countdown', icon: 'bi-hourglass-split', label: t('nav.countdown') },
        { to: '/dashboard/wedding-progress', icon: 'bi-graph-up-arrow', label: t('nav.progressTracker') },
        { to: '/dashboard/ceremony-templates', icon: 'bi-building', label: t('nav.ceremonyTemplates') },
        { to: '/dashboard/rituals', icon: 'bi-fire', label: t('nav.rituals') },
        { to: '/dashboard/baraat-vidaai', icon: 'bi-signpost-2', label: t('nav.baraatVidaai') },
        { to: '/dashboard/wedding-week', icon: 'bi-calendar-week', label: t('nav.weddingWeek') },
        { to: '/dashboard/planning-boards', icon: 'bi-kanban', label: t('nav.planningBoards'), plan: 'silver' },
        { to: '/dashboard/rehearsal', icon: 'bi-calendar-event', label: t('nav.rehearsal') },
      ],
    },
    {
      label: t('nav.groupGuests'),
      items: [
        { to: '/dashboard/guests', icon: 'bi-people', label: t('nav.guestList') },
        { to: '/dashboard/seating-chart', icon: 'bi-grid-3x3-gap', label: t('nav.seatingChart'), plan: 'platinum' },
        { to: '/dashboard/side-management', icon: 'bi-arrow-left-right', label: t('nav.sideManagement') },
        { to: '/dashboard/rsvp-analytics', icon: 'bi-pie-chart', label: t('nav.rsvpAnalytics'), plan: 'platinum' },
        { to: '/dashboard/guest-messages', icon: 'bi-whatsapp', label: t('nav.guestMessages') },
      ],
    },
    {
      label: t('nav.groupInvitations'),
      items: [
        { to: '/dashboard/invitation-builder', icon: 'bi-envelope-heart', label: t('nav.invitations'), plan: 'silver' },
        { to: '/dashboard/qr-invites', icon: 'bi-qr-code', label: t('nav.qrInvites') },
        { to: '/dashboard/family-portal', icon: 'bi-people-fill', label: t('nav.familyPortal'), plan: 'silver' },
        { to: '/dashboard/family-chat', icon: 'bi-chat-dots-fill', label: t('nav.familyChat'), comingSoon: true },
      ],
    },
    {
      label: t('nav.groupVendors'),
      items: [
        { to: '/dashboard/bookings', icon: 'bi-calendar-check', label: t('nav.bookingsVendors') },
        { to: '/dashboard/vendor-calendar', icon: 'bi-calendar2-check', label: t('nav.vendorCalendarNav'), plan: 'silver' },
        { to: '/dashboard/vendor-checkins', icon: 'bi-qr-code', label: t('nav.vendorCheckIn') },
        { to: '/dashboard/coordinators', icon: 'bi-person-badge', label: 'Coordinators' },
        { to: '/dashboard/coordinator-messages', icon: 'bi-chat-left-dots', label: 'Coordinator Messages' },
      ],
    },
    {
      label: t('nav.groupDesignMedia'),
      items: [
        { to: '/dashboard/photo-wall', icon: 'bi-images', label: t('nav.photoWall'), plan: 'gold' },
        { to: '/dashboard/photo-tags', icon: 'bi-tags', label: t('nav.photoTags'), comingSoon: true },
        { to: '/dashboard/mood-board', icon: 'bi-palette', label: t('nav.moodBoard'), plan: 'gold' },
        { to: '/dashboard/attire', icon: 'bi-scissors', label: t('nav.attirePlanner') },
        { to: '/dashboard/caricature', icon: 'bi-brush', label: t('nav.caricature'), plan: 'gold' },
        { to: '/dashboard/website', icon: 'bi-globe', label: t('nav.websiteBuilder') },
      ],
    },
    {
      label: t('nav.groupTools'),
      items: [
        { to: '/dashboard/speeches', icon: 'bi-mic', label: t('nav.speechesToasts') },
        { to: '/dashboard/currency', icon: 'bi-currency-exchange', label: t('nav.currency') },
        { to: '/dashboard/analytics', icon: 'bi-graph-up', label: t('nav.analytics'), plan: 'platinum' },
      ],
    },
    {
      label: t('nav.groupDayOf'),
      items: [
        { to: '/dashboard/live-feed', icon: 'bi-broadcast', label: t('nav.liveFeed'), plan: 'platinum' },
        { to: '/dashboard/accommodations', icon: 'bi-building', label: t('nav.accommodations'), plan: 'silver' },
        { to: '/dashboard/arrivals', icon: 'bi-airplane-engines', label: t('nav.arrivalsPickups'), plan: 'silver' },
        { to: '/dashboard/hospitality-desk', icon: 'bi-bell', label: t('nav.hospitalityDesk'), plan: 'silver' },
        { to: '/dashboard/gift-registry', icon: 'bi-gift', label: t('nav.giftRegistry'), plan: 'gold' },
        { to: '/dashboard/gifts-received', icon: 'bi-envelope-paper-heart', label: t('nav.giftsReceived'), plan: 'silver' },
        { to: '/dashboard/emergency-contacts', icon: 'bi-telephone-plus', label: t('nav.emergencyContacts') },
        { to: '/dashboard/honeymoon', icon: 'bi-airplane', label: t('nav.honeymoon') },
        { to: '/dashboard/post-wedding', icon: 'bi-heart-arrow', label: t('nav.afterWedding'), plan: 'platinum' },
        { to: '/dashboard/first-year', icon: 'bi-calendar-heart', label: t('nav.firstYear') },
      ],
    },
    {
      label: t('nav.groupDocuments'),
      items: [
        { to: '/dashboard/document-vault', icon: 'bi-safe', label: t('nav.documentVault'), plan: 'silver' },
        { to: '/dashboard/timeline-export', icon: 'bi-printer', label: t('nav.timelineExport'), plan: 'platinum' },
        { to: '/dashboard/data-export', icon: 'bi-shield-lock', label: t('nav.dataPrivacy') },
      ],
    },
    {
      label: t('nav.groupAccount'),
      items: [
        { to: '/dashboard/messages', icon: 'bi-chat', label: t('nav.messages') },
        { to: '/dashboard/notification-rules', icon: 'bi-bell-fill', label: t('nav.smartAlerts') },
        { to: '/dashboard/billing', icon: 'bi-credit-card', label: t('nav.billingPlans') },
        { to: '/dashboard/settings', icon: 'bi-gear', label: t('nav.settings') },
        { to: '/dashboard/affiliate', icon: 'bi-people', label: t('nav.affiliate') },
      ],
    },
  ];

  // ── Vendor nav groups (6 groups) ───────────────────────────────────────────
  const vendorNavGroups = [
    {
      label: null,
      items: [
        { to: '/vendor', icon: 'bi-house', label: t('nav.vendorDashboard'), end: true },
      ],
    },
    {
      label: t('nav.groupBusiness'),
      items: [
        { to: '/vendor/profile', icon: 'bi-person-badge', label: t('nav.vendorProfile') },
        { to: '/vendor/portfolio', icon: 'bi-collection', label: t('nav.vendorPortfolio') },
        { to: '/vendor/analytics', icon: 'bi-bar-chart-line', label: t('nav.vendorAnalytics') },
        { to: '/vendor/subscription', icon: 'bi-gem', label: t('nav.vendorSubscriptionNav') },
      ],
    },
    {
      label: t('nav.groupBookingsCalendar'),
      items: [
        { to: '/vendor/bookings', icon: 'bi-calendar-check', label: t('nav.vendorBookings') },
        { to: '/vendor/calendar', icon: 'bi-calendar3', label: t('nav.vendorCalendar') },
        { to: '/vendor/quotes', icon: 'bi-receipt', label: t('nav.vendorQuotes') },
      ],
    },
    {
      label: t('nav.groupClientsLeads'),
      items: [
        { to: '/vendor/messages', icon: 'bi-chat', label: t('nav.vendorMessages') },
        { to: '/vendor/crm', icon: 'bi-kanban', label: t('nav.vendorCrm') },
      ],
    },
    {
      label: t('nav.groupOperations'),
      items: [
        { to: '/vendor/pricing', icon: 'bi-graph-up-arrow', label: t('nav.vendorPricing') },
        { to: '/vendor/team', icon: 'bi-people', label: t('nav.vendorTeam') },
      ],
    },
    {
      label: t('nav.groupSettings'),
      items: [
        { to: '/vendor/settings', icon: 'bi-gear', label: t('nav.vendorSettings') },
      ],
    },
  ];

  // ── Admin nav groups (7 groups) ────────────────────────────────────────────
  const adminNavGroups = [
    {
      label: null,
      items: [
        { to: '/admin', icon: 'bi-shield-lock', label: 'Dashboard', end: true },
      ],
    },
    {
      label: 'People & Platform',
      items: [
        { to: '/admin/users', icon: 'bi-person-lines-fill', label: 'Users' },
        { to: '/admin/coordinator-assignments', icon: 'bi-person-badge', label: 'Coordinators' },
        { to: '/admin/coordinator-approvals', icon: 'bi-person-check', label: 'Coordinator Approvals' },
        { to: '/admin/planners', icon: 'bi-briefcase', label: 'Planner Access' },
        { to: '/admin/vendors', icon: 'bi-shop', label: 'Vendors' },
        { to: '/admin/vendor-review', icon: 'bi-ui-checks', label: 'Vendor Approvals' },
        { to: '/admin/venues', icon: 'bi-building', label: 'Venues' },
        { to: '/admin/bookings', icon: 'bi-calendar-check', label: 'Bookings' },
      ],
    },
    {
      label: 'Finance',
      items: [
        { to: '/admin/revenue', icon: 'bi-graph-up', label: 'Revenue' },
        { to: '/admin/subscription-lifecycle', icon: 'bi-arrow-repeat', label: 'Subscriptions' },
        ...(user?.role === 'super_admin' ? [{ to: '/admin/pricing', icon: 'bi-tag', label: 'Pricing Config' }] : []),
      ],
    },
    {
      label: 'Insights',
      items: [
        { to: '/admin/analytics', icon: 'bi-graph-up-arrow', label: 'Analytics' },
        { to: '/admin/platform-metrics', icon: 'bi-bar-chart-line', label: 'Metrics' },
        { to: '/admin/onboarding-funnel', icon: 'bi-funnel', label: 'Onboarding Funnel' },
        { to: '/admin/email-deliverability', icon: 'bi-envelope-check', label: 'Email Deliverability' },
      ],
    },
    {
      label: 'Content & Trust',
      items: [
        { to: '/admin/moderation', icon: 'bi-shield-check', label: 'Moderation' },
        { to: '/admin/reviews', icon: 'bi-chat-square-text', label: 'Reviews' },
        { to: '/admin/gallery', icon: 'bi-images', label: 'Gallery' },
        { to: '/admin/banners', icon: 'bi-megaphone', label: 'Banners' },
        { to: '/admin/categories', icon: 'bi-tags', label: 'Categories' },
        { to: '/admin/bulk-actions', icon: 'bi-collection', label: 'Bulk Actions' },
      ],
    },
    {
      label: 'Content Management',
      items: [
        { to: '/admin/blog', icon: 'bi-journal-richtext', label: 'Blog Posts' },
        { to: '/admin/real-weddings', icon: 'bi-camera-reels', label: 'Real Weddings' },
        { to: '/admin/auspicious-dates', icon: 'bi-calendar-heart', label: 'Auspicious Dates' },
        { to: '/admin/cost-guides', icon: 'bi-cash-coin', label: 'City Cost Guides' },
        { to: '/admin/vendor-scraper', icon: 'bi-robot', label: 'Vendor Scraper' },
        { to: '/admin/venue-scraper',  icon: 'bi-buildings', label: 'Venue Scraper' },
      ],
    },
    {
      label: 'Communications',
      items: [
        { to: '/admin/broadcast', icon: 'bi-megaphone-fill', label: 'Broadcast' },
        { to: '/admin/help-requests', icon: 'bi-headset', label: 'Callback Requests' },
        { to: '/admin/contact-messages', icon: 'bi-envelope-paper', label: 'Contact Messages' },
      ],
    },
    {
      label: 'System & Config',
      items: [
        { to: '/admin/settings', icon: 'bi-gear', label: 'Settings' },
        { to: '/admin/payment-settings', icon: 'bi-credit-card', label: 'Payment Gateways' },
        { to: '/admin/integration-settings', icon: 'bi-plug', label: 'Integrations' },
        { to: '/admin/security-settings', icon: 'bi-shield-lock', label: 'Security' },
        { to: '/admin/system-health', icon: 'bi-heart-pulse', label: 'System Health' },
        { to: '/admin/audit', icon: 'bi-journal-text', label: 'Audit Log' },
        { to: '/admin/feature-flags', icon: 'bi-toggles', label: 'Feature Flags' },
        { to: '/admin/export', icon: 'bi-download', label: 'Data Export' },
        // Single entry — combines the old Live Docs + Feature Roadmap
        // pages with a "View by Phase / Role" toggle on the page itself.
        // /admin/docs still works as a deep link defaulting to the role view.
        // Gated to super_admin: roadmap is strategic/internal, not for ordinary
        // platform admins. Route-level guard in App.jsx is the source of truth;
        // hiding the nav entry just avoids dead links.
        ...(user?.role === 'super_admin' ? [
          { to: '/admin/roadmap', icon: 'bi-clipboard-check', label: 'Implementation Tracker' },
        ] : []),
      ],
    },
  ];

  // ── Venue Owner nav groups (4 groups) ──────────────────────────────────────
  const venueOwnerNavGroups = [
    {
      label: null,
      items: [
        { to: '/venue', icon: 'bi-house', label: 'Dashboard', end: true },
      ],
    },
    {
      label: 'Venue',
      items: [
        { to: '/venue/profile', icon: 'bi-building', label: 'Venue Profile' },
        { to: '/venue/photos', icon: 'bi-images', label: 'Photos' },
        { to: '/venue/amenities', icon: 'bi-check2-square', label: 'Amenities' },
        { to: '/venue/floor-plans', icon: 'bi-grid-3x3', label: 'Floor Plans' },
      ],
    },
    {
      label: 'Bookings',
      items: [
        { to: '/venue/availability', icon: 'bi-calendar-x', label: 'Availability' },
        { to: '/venue/bookings', icon: 'bi-calendar-check', label: 'Bookings' },
        { to: '/venue/inquiries', icon: 'bi-funnel', label: 'Inquiries' },
      ],
    },
    {
      label: 'Feedback',
      items: [
        { to: '/venue/reviews', icon: 'bi-star', label: 'Reviews' },
      ],
    },
  ];

  // ── Coordinator nav groups (4 groups) ──────────────────────────────────────
  const coordinatorNavGroups = [
    {
      label: null,
      items: [
        { to: '/coordinator', icon: 'bi-clipboard2-check', label: 'Dashboard', end: true },
      ],
    },
    {
      label: 'Weddings',
      items: [
        { to: '/coordinator/requests', icon: 'bi-inbox', label: 'Booking Requests' },
        { to: '/coordinator/messages', icon: 'bi-chat-left-dots', label: 'Couple Messages' },
        { to: '/coordinator/weddings', icon: 'bi-heart', label: 'My Weddings' },
        { to: '/coordinator/enquiries', icon: 'bi-envelope-heart', label: 'Enquiries' },
        { to: '/coordinator/clients', icon: 'bi-person-plus', label: 'Clients' },
        { to: '/coordinator/invoices', icon: 'bi-receipt', label: 'Invoices' },
        { to: '/coordinator/profit', icon: 'bi-graph-up-arrow', label: 'Profit' },
        { to: '/coordinator/team', icon: 'bi-people', label: 'Team & Roster' },
        { to: '/coordinator/contracts', icon: 'bi-file-earmark-text', label: 'Contracts' },
        { to: '/coordinator/calendar', icon: 'bi-calendar3', label: 'Calendar' },
        { to: '/coordinator/multi-dashboard', icon: 'bi-grid-3x3-gap', label: 'Multi-Wedding' },
      ],
    },
    {
      label: 'Network',
      items: [
        { to: '/coordinator/vendors', icon: 'bi-shop', label: "Coordinator's Network" },
        { to: '/coordinator/white-label', icon: 'bi-brush', label: 'White-Label' },
      ],
    },
    {
      label: 'Marketplace',
      items: [
        { to: '/coordinator/profile', icon: 'bi-person-badge', label: 'Public Profile' },
        { to: '/coordinator/availability', icon: 'bi-calendar2-week', label: 'Availability' },
      ],
    },
    {
      label: 'Settings',
      items: [
        { to: '/coordinator/settings', icon: 'bi-gear', label: 'Settings' },
      ],
    },
  ];

  // ── Flat lists derived from groups (used for search & MobileBottomNav) ────
  const activeNavGroups = (() => {
    if (type === 'vendor') return vendorNavGroups;
    if (type === 'admin') return adminNavGroups;
    if (type === 'venue_owner') return venueOwnerNavGroups;
    if (type === 'coordinator') return coordinatorNavGroups;
    // Inside a client's wedding, only the screens that work there.
    if (acting) {
      return coupleNavGroups
        .map(g => ({ ...g, items: g.items.filter(item => isActingPath(item.to)) }))
        .filter(g => g.items.length > 0);
    }
    return coupleNavGroups;
  })();
  const navItems = activeNavGroups.flatMap(g => g.items);

  // Accordion follow-the-route: when the current path changes (initial
  // mount, in-app navigation, role-switch), collapse every group except the
  // one containing the active route. We intentionally depend ONLY on the
  // pathname + role — `activeNavGroups` is a fresh array on every render,
  // so including it in the deps would re-run the effect each render and
  // clobber the manual toggleGroup() state. The label set + per-route
  // lookup are re-derived inside the effect body so this stays correct
  // when the role's sidebar changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!activeNavGroups?.length) return;
    const labels = activeNavGroups.filter(g => g.label).map(g => g.label);
    if (!labels.length) return;
    const path = location.pathname;
    const matchItem = it => {
      if (!it.to) return false;
      if (it.end) return path === it.to;
      return path === it.to || path.startsWith(it.to + '/');
    };
    // Score by longest matching `to` so nested routes (e.g. /admin/venues
    // under /admin) land in the right group instead of always matching the
    // dashboard root.
    let bestLabel = null;
    let bestLen = -1;
    activeNavGroups.forEach(g => {
      if (!g.label) return;
      g.items?.forEach(it => {
        if (matchItem(it) && it.to.length > bestLen) {
          bestLen = it.to.length;
          bestLabel = g.label;
        }
      });
    });
    if (!bestLabel) bestLabel = activeNavGroups.find(g => g.label)?.label;
    const next = {};
    labels.forEach(l => { next[l] = l !== bestLabel; });
    setClosedGroups(next);
  }, [location.pathname, type]);


  return (
    <div className={`dashboard${(wlPrimary || wlAccent) ? ' dashboard--wl' : ''}`}>
      {(wlPrimary || wlAccent) && (
        <style>{`
          ${wlPrimary ? `.dashboard--wl .dashboard__nav-item.active{background:${wlPrimary}1f;color:${wlPrimary};border-left-color:${wlPrimary};}
          .dashboard--wl .dashboard__nav-item.active i{color:${wlPrimary};}` : ''}
          .dashboard--wl .dashboard__nav-item:hover{background:${wlAccent || wlPrimary}14;}
          .dashboard--wl .dashboard__nav-item:hover i{color:${wlAccent || wlPrimary};}
        `}</style>
      )}
      {/* Sidebar */}
      <aside className={`dashboard__sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="px-2 pt-2">
          <PromoBannerDisplay placement="sidebar" maxBanners={1} />
        </div>
        {/* Navigation */}
        <nav className="dashboard__nav">
          {/* ── Grouped nav (all roles) ── */}
          {activeNavGroups.map((group, gi) => {
            const isCollapsed = !!group.label && !!closedGroups[group.label];
            return (
              <div key={gi}>
                {group.label && (
                  <div className="dashboard__nav-group-header" onClick={() => toggleGroup(group.label)}>
                    <span>{group.label}</span>
                    <i className={`bi bi-chevron-${isCollapsed ? 'right' : 'down'}`} />
                  </div>
                )}
                {!isCollapsed && group.items.map(item => {
                  // Plan-lock logic (couple only)
                  const featureKey = type === 'couple' ? {
                    '/dashboard/ai-concierge': 'aiConcierge',
                    '/dashboard/ai-content-writer': 'aiWriter',
                    '/dashboard/email-drafter': 'aiConcierge',
                    '/dashboard/planning-boards': 'planningBoards',
                    '/dashboard/destination-calculator': 'destinationCalculator',
                    '/dashboard/invitation-builder': 'invitationBuilder',
                    '/dashboard/photo-wall': 'photoWall',
                    '/dashboard/photo-tags': 'photoWall',
                    '/dashboard/gift-registry': 'giftRegistry',
                    '/dashboard/vendor-calendar': 'vendorCalendar',
                    '/dashboard/vendor-matcher': 'vendorCompare',
                    '/dashboard/budget-optimizer': 'budgetOptimizer',
                    '/dashboard/vendor-compare': 'vendorCompare',
                    '/dashboard/mood-board': 'photoWall',
                    '/dashboard/document-vault': 'documentVault',
                    '/dashboard/accommodations': 'accommodationManager',
                    '/dashboard/arrivals': 'guestTravel',
                    '/dashboard/gifts-received': 'giftLog',
                    '/dashboard/family-portal': 'familyPortal',
                    '/dashboard/seating-chart': 'seatingChart',
                    '/dashboard/analytics': 'analytics',
                    '/dashboard/timeline-export': 'timelineExport',
                    '/dashboard/post-wedding': 'postWedding',
                    '/dashboard/live-feed': 'liveTimeline',
                    '/dashboard/rsvp-analytics': 'analytics',
                    '/dashboard/guest-optimizer': 'analytics',
                  }[item.to] : null;
                  const locked = featureKey ? !hasFeature(featureKey) : false;
                  return (
                    <NavLink
                      key={item.to} to={item.to} end={item.end}
                      className={({ isActive }) => `dashboard__nav-item ${isActive ? 'active' : ''} ${locked ? 'locked' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <i className={`bi ${item.icon}`}></i>
                      <span>{item.label}</span>
                      {item.comingSoon && (
                        // Phase-2 deferred feature (Photo Tags / Family Chat per
                        // 2026-06-03 QA review). Render a SOON pill so the
                        // sidebar entry sets expectations before the user
                        // clicks through to the ComingSoon page.
                        <span className="dashboard__nav-lock" title="Coming soon">
                          <span style={{ fontSize: '0.55rem', background: 'linear-gradient(135deg,#9B30FF,#D4A843)', color: '#fff', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>
                            SOON
                          </span>
                        </span>
                      )}
                      {!item.comingSoon && locked && item.plan && (
                        <span className="dashboard__nav-lock" title={`${item.plan.charAt(0).toUpperCase() + item.plan.slice(1)} plan required`}>
                          <span style={{ fontSize: '0.55rem', background: item.plan === 'platinum' ? '#6366F1' : item.plan === 'gold' ? '#D4A843' : '#9CA3AF', color: '#fff', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>
                            {item.plan.toUpperCase()}
                          </span>
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}

        </nav>

      </aside>

      {/* Main Content */}
      <main className="dashboard__main">
        {/* Top Header Bar */}
        <header className="dashboard__header" style={{ display: 'flex', alignItems: 'center', padding: '0 16px', height: 56, borderBottom: '1px solid #E5E7EB', background: wlHeaderBg || '#fff', position: 'sticky', top: 0, zIndex: 50, gap: 8 }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginRight: 4 }}>
            <img src={wlLogo || logoImg} alt={wlBrand || 'VowsEra'} style={{ height: 45, maxWidth: 160, objectFit: 'contain' }} onError={e => { e.target.onerror = null; e.target.src = logoImg; }} />
            {wlBrand && <span style={{ fontWeight: 700, fontSize: '1rem', color: wlHeaderText || '#1D1D44', whiteSpace: 'nowrap' }}>{wlBrand}</span>}
          </a>
          <button
            className="dashboard__menu-btn d-md-none"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <i className={`bi bi-${sidebarOpen ? 'x' : 'list'}`}></i>
          </button>
          {type === 'admin' ? <AdminSearchBar /> : <GlobalSearchBar variant="header" />}
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {(type === 'couple' || type === 'vendor') && (() => {
              // Vendors carry their own subscription tier (free/pro/premium)
              // — read from vendor_subscriptions. Couples read the silver/
              // gold/platinum tier PlanContext loads. The badge + click-through
              // link adapt per role.
              const displayPlan = type === 'vendor' ? (vendorPlan || 'free') : (plan || 'free');
              const planLink    = type === 'vendor' ? '/vendor/subscription' : '/dashboard/billing';
              const bg =
                  displayPlan === 'platinum' || displayPlan === 'premium' ? 'linear-gradient(135deg, #6366F1, #9B30FF)'
                : displayPlan === 'gold'                                  ? 'linear-gradient(135deg, #D4A843, #F59E0B)'
                : displayPlan === 'silver' || displayPlan === 'pro'       ? 'linear-gradient(135deg, #3B82F6, #2563EB)'
                : '#F3F4F6';
              return (
                <Link to={planLink} style={{ textDecoration: 'none' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: bg, color: displayPlan === 'free' ? '#6B7280' : '#fff', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {displayPlan} {displayPlan === 'free' && <span style={{ fontSize: '0.6rem' }}>↑</span>}
                  </span>
                </Link>
              );
            })()}
            <LanguageSwitcher variant="inline" />
            <NotificationBell />
            {/* User dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setUserDropdown(p => !p)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}
              >
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: wlPrimary || '#9B30FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.8rem', position: 'relative', overflow: 'hidden' }}>
                  {/* Initials are the base layer; the avatar image overlays it.
                      If the image URL fails, onError hides it so initials show. */}
                  <span>{`${(user?.firstName || user?.first_name || '')[0] || ''}${(user?.lastName || user?.last_name || '')[0] || ''}`}</span>
                  {user?.avatar && (
                    <img key={user.avatar} src={resolveStorageUrl(user.avatar)} alt=""
                      style={{ position: 'absolute', inset: 0, width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                </div>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: wlHeaderText || '#374151' }} className="d-none d-md-inline">
                  {user?.firstName || user?.first_name} {user?.lastName || user?.last_name}
                </span>
                <i className={`bi bi-chevron-${userDropdown ? 'up' : 'down'} d-none d-md-inline`} style={{ fontSize: '0.7rem', color: wlHeaderText || '#6B7280' }} />
              </button>
              {userDropdown && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setUserDropdown(false)} />
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: '#fff', borderRadius: 10, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', border: '1px solid #E5E7EB', minWidth: 220, zIndex: 100, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111' }}>{user?.firstName || user?.first_name} {user?.lastName || user?.last_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{user?.email}</div>
                      <span style={{ fontSize: '0.65rem', textTransform: 'capitalize', color: '#9B30FF', fontWeight: 600 }}>{user?.role === 'user' ? 'Couple' : (user?.role || '').replace('_', ' ')}</span>
                    </div>
                    {(() => {
                      // Both items land on the personal Settings page but on
                      // different tabs (?tab=profile vs ?tab=account). The base
                      // must be the page that actually HAS those tabs — for an
                      // admin that's /admin/account, NOT /admin/settings (which
                      // is the platform-wide System Settings page).
                      const base =
                        type === 'admin'       ? '/admin/account'
                        : type === 'vendor'      ? '/vendor/settings'
                        : type === 'coordinator' ? '/coordinator/settings'
                        : type === 'venue_owner' ? '/venue/settings'
                        : '/dashboard/settings';
                      return [
                        { icon: 'bi-person', label: 'My Profile', to: `${base}?tab=profile` },
                        { icon: 'bi-gear',   label: 'Settings',   to: `${base}?tab=account` },
                      ];
                    })().map(item => (
                      <Link key={item.to} to={item.to} onClick={() => setUserDropdown(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', fontSize: '0.82rem', color: '#374151', textDecoration: 'none', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <i className={`bi ${item.icon}`} style={{ fontSize: '0.9rem', color: '#6B7280' }} />{item.label}
                      </Link>
                    ))}
                    <div style={{ borderTop: '1px solid #F3F4F6' }}>
                      <button onClick={() => { setUserDropdown(false); handleLogout(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', fontSize: '0.82rem', color: '#EF4444', background: 'none', border: 'none', width: '100%', cursor: 'pointer', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <i className="bi bi-box-arrow-right" style={{ fontSize: '0.9rem' }} />{t('common.logout')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard__content">
          {/*
            Promotional banner — admin-controlled "Dashboard Top"
            placement. Previously this slot was empty (the older site-
            wide banner was removed per a #1 change request), so any
            banner an admin saved with placement="dashboard_top" was
            orphaned — visible as Active in /admin/banners, but with no
            actual render path. Restoring the slot for that single
            placement reconnects the admin UI to the dashboard while
            leaving the older site-wide top_bar slot (handled in
            MainLayout) untouched.
          */}
          {acting && (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 mb-3 rounded" role="status"
              style={{ background: '#2D1B69', color: '#fff' }}>
              <span className="small">
                <i className="bi bi-briefcase me-2" />
                You are working in <strong>{acting.label}</strong>. Changes you make are recorded against your name.
              </span>
              <button type="button" className="btn btn-sm btn-light" onClick={leaveWedding}>Back to my weddings</button>
            </div>
          )}
          <PromoBannerDisplay placement="dashboard_top" maxBanners={1} />
          {acting && !isActingPath(location.pathname) ? (
            <div className="card border-0 shadow-sm p-4 text-center">
              <div style={{ fontSize: '2rem' }}>🔒</div>
              <h1 className="h5 mt-2">This page is the couple's own</h1>
              <p className="text-muted small mb-3">Billing, account settings and personal pages stay with the couple. Everything in the menu on the left works for you here.</p>
              <div><Link to="/dashboard" className="btn btn-sm btn-outline-secondary">Go to the wedding dashboard</Link></div>
            </div>
          ) : type === 'couple' ? <WeddingProvider><Outlet /></WeddingProvider> : <Outlet />}
        </div>

        {/* White-label footer text */}
        {wlFooter && (
          <footer style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.75rem', color: '#9CA3AF', borderTop: '1px solid #E5E7EB' }}>
            {wlFooter}
          </footer>
        )}

        {/* Real-time toast notifications overlay */}
        <RealtimeToastOverlay />

        {/* Friendly "create your wedding first" banner when APIs return NO_WEDDING */}
        <NoWeddingPrompt />
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav navItems={navItems} type={type} />

      {/* PWA Install Prompt */}
      <PwaInstallPrompt />

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="dashboard__overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
